/**
 * Imperative confirm / prompt / toast utilities backed by a single
 * `<DialogHost />` component mounted near the root of the app.
 *
 * Callers don't need React context — they can invoke from event handlers,
 * hooks, plain async functions, etc:
 *
 *   if (!(await confirmDialog({ body: 'Delete this?' }))) return;
 *   const name = await promptDialog({ title: 'Name this view' });
 *   toast.success('Saved');
 *
 * Why a module-level emitter (vs React context)?
 * - Many call sites are inside `useCallback`/`useMemo` chains or non-React
 *   helpers (`useEventApproval.tsx`, `useRecalculateDate.ts`) where pulling
 *   in a context hook would force them to thread the hook through. The
 *   imperative API is a drop-in replacement for `window.confirm/alert/prompt`.
 * - Behavior is centralized: focus management, ESC/Enter handling, dark-mode
 *   styling and a single Bootstrap-themed look all live in one component.
 */

import { useEffect, useRef, useState } from 'react';

// ────────────────────────────────────────────────────────────────────────
// Public types
// ────────────────────────────────────────────────────────────────────────

export type ConfirmOptions = {
  title?: string;
  /** Body text. Plain string only — keep it short. */
  body: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Render the confirm button in danger styling. Defaults to false. */
  danger?: boolean;
};

export type PromptOptions = {
  title?: string;
  body?: string;
  label?: string;
  defaultValue?: string;
  placeholder?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Optional inline validator. Return an error string to block submission,
   *  or null/undefined to allow it. Runs on every keystroke. */
  validate?: (value: string) => string | null | undefined;
};

export type ToastKind = 'success' | 'error' | 'info' | 'warning';

export type ToastOptions = {
  /** Auto-dismiss timeout in ms. Defaults to 5000. Pass 0 to make sticky. */
  durationMs?: number;
};

// ────────────────────────────────────────────────────────────────────────
// Internal store + emitter
// ────────────────────────────────────────────────────────────────────────

type ConfirmRequest = ConfirmOptions & { resolve: (ok: boolean) => void };
type PromptRequest = PromptOptions & { resolve: (value: string | null) => void };
type ToastEntry = { id: number; kind: ToastKind; message: string; durationMs: number };

type Listener = () => void;

class DialogStore {
  confirmReq: ConfirmRequest | null = null;
  promptReq: PromptRequest | null = null;
  toasts: ToastEntry[] = [];
  private listeners = new Set<Listener>();
  private nextToastId = 1;

  subscribe(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  private emit(): void {
    for (const l of this.listeners) l();
  }

  openConfirm(opts: ConfirmOptions): Promise<boolean> {
    return new Promise((resolve) => {
      // If a previous prompt/confirm is still open, auto-cancel it so we
      // never end up with two stacked dialogs (extremely unlikely, but it
      // preserves the contract that callers always get a resolution).
      if (this.confirmReq) this.confirmReq.resolve(false);
      this.confirmReq = { ...opts, resolve };
      this.emit();
    });
  }

  resolveConfirm(ok: boolean): void {
    const req = this.confirmReq;
    if (!req) return;
    this.confirmReq = null;
    this.emit();
    req.resolve(ok);
  }

  openPrompt(opts: PromptOptions): Promise<string | null> {
    return new Promise((resolve) => {
      if (this.promptReq) this.promptReq.resolve(null);
      this.promptReq = { ...opts, resolve };
      this.emit();
    });
  }

  resolvePrompt(value: string | null): void {
    const req = this.promptReq;
    if (!req) return;
    this.promptReq = null;
    this.emit();
    req.resolve(value);
  }

  pushToast(kind: ToastKind, message: string, opts: ToastOptions = {}): number {
    const id = this.nextToastId++;
    const durationMs = opts.durationMs ?? 5000;
    this.toasts = [...this.toasts, { id, kind, message, durationMs }];
    this.emit();
    return id;
  }

  dismissToast(id: number): void {
    this.toasts = this.toasts.filter((t) => t.id !== id);
    this.emit();
  }
}

const store = new DialogStore();

// ────────────────────────────────────────────────────────────────────────
// Imperative public API
// ────────────────────────────────────────────────────────────────────────

export function confirmDialog(opts: ConfirmOptions): Promise<boolean> {
  return store.openConfirm(opts);
}

export function promptDialog(opts: PromptOptions): Promise<string | null> {
  return store.openPrompt(opts);
}

export const toast = {
  success: (message: string, opts?: ToastOptions) => store.pushToast('success', message, opts),
  error: (message: string, opts?: ToastOptions) => store.pushToast('error', message, opts),
  info: (message: string, opts?: ToastOptions) => store.pushToast('info', message, opts),
  warning: (message: string, opts?: ToastOptions) => store.pushToast('warning', message, opts),
  dismiss: (id: number) => store.dismissToast(id),
};

// ────────────────────────────────────────────────────────────────────────
// Components
// ────────────────────────────────────────────────────────────────────────

/** Tiny hook that re-renders any component that subscribes to the store. */
function useStoreSnapshot(): { confirmReq: ConfirmRequest | null; promptReq: PromptRequest | null; toasts: ToastEntry[] } {
  const [, setTick] = useState(0);
  useEffect(() => store.subscribe(() => setTick((t) => t + 1)), []);
  return { confirmReq: store.confirmReq, promptReq: store.promptReq, toasts: store.toasts };
}

/**
 * Mount once near the root of the app (after <App />). Renders the
 * confirm modal, the prompt modal, and the toast region.
 */
export function DialogHost(): React.JSX.Element {
  const { confirmReq, promptReq, toasts } = useStoreSnapshot();
  return (
    <>
      {confirmReq && <ConfirmModal req={confirmReq} />}
      {promptReq && <PromptModal req={promptReq} />}
      <ToastRegion toasts={toasts} />
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Confirm modal
// ────────────────────────────────────────────────────────────────────────

function ConfirmModal({ req }: { req: ConfirmRequest }): React.JSX.Element {
  const confirmBtnRef = useRef<HTMLButtonElement | null>(null);

  // Autofocus the primary action so Enter resolves true.
  useEffect(() => {
    confirmBtnRef.current?.focus();
  }, []);

  // ESC cancels, Enter confirms. Keyboard handlers are attached at window
  // level so they work regardless of which descendant has focus.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        store.resolveConfirm(false);
      } else if (e.key === 'Enter' && e.target instanceof HTMLElement && e.target.tagName !== 'TEXTAREA') {
        e.preventDefault();
        store.resolveConfirm(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const titleId = 'mh-confirm-title';
  const bodyId = 'mh-confirm-body';
  return (
    <>
      <div className="modal-backdrop fade show" onClick={() => store.resolveConfirm(false)} />
      <div
        className="modal fade show d-block"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title h5" id={titleId}>{req.title ?? 'Confirm'}</h2>
              <button
                type="button"
                className="btn-close"
                aria-label="Cancel"
                onClick={() => store.resolveConfirm(false)}
              />
            </div>
            <div className="modal-body">
              <p className="mb-0" id={bodyId} style={{ whiteSpace: 'pre-line' }}>{req.body}</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => store.resolveConfirm(false)}
              >
                {req.cancelLabel ?? 'Cancel'}
              </button>
              <button
                ref={confirmBtnRef}
                type="button"
                className={`btn ${req.danger ? 'btn-danger' : 'btn-primary'}`}
                onClick={() => store.resolveConfirm(true)}
              >
                {req.confirmLabel ?? 'OK'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Prompt modal
// ────────────────────────────────────────────────────────────────────────

function PromptModal({ req }: { req: PromptRequest }): React.JSX.Element {
  const [value, setValue] = useState<string>(req.defaultValue ?? '');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const error = req.validate?.(value) ?? null;
  const canSubmit = !error;

  function submit(): void {
    if (!canSubmit) return;
    store.resolvePrompt(value);
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        store.resolvePrompt(null);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const titleId = 'mh-prompt-title';
  const labelId = 'mh-prompt-label';
  const errorId = 'mh-prompt-error';
  return (
    <>
      <div className="modal-backdrop fade show" onClick={() => store.resolvePrompt(null)} />
      <div
        className="modal fade show d-block"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title h5" id={titleId}>{req.title ?? 'Enter a value'}</h2>
              <button
                type="button"
                className="btn-close"
                aria-label="Cancel"
                onClick={() => store.resolvePrompt(null)}
              />
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submit();
              }}
            >
              <div className="modal-body">
                {req.body && <p className="mb-3" style={{ whiteSpace: 'pre-line' }}>{req.body}</p>}
                {req.label && (
                  <label htmlFor="mh-prompt-input" className="form-label" id={labelId}>
                    {req.label}
                  </label>
                )}
                <input
                  ref={inputRef}
                  id="mh-prompt-input"
                  type="text"
                  className={`form-control${error ? ' is-invalid' : ''}`}
                  value={value}
                  placeholder={req.placeholder}
                  onChange={(e) => setValue(e.target.value)}
                  aria-describedby={error ? errorId : undefined}
                  aria-labelledby={req.label ? labelId : undefined}
                />
                {error && (
                  <div className="invalid-feedback d-block" id={errorId}>{error}</div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => store.resolvePrompt(null)}
                >
                  {req.cancelLabel ?? 'Cancel'}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!canSubmit}
                >
                  {req.confirmLabel ?? 'OK'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Toasts
// ────────────────────────────────────────────────────────────────────────

const TOAST_ICON: Record<ToastKind, string> = {
  success: 'bi-check-circle-fill',
  error: 'bi-exclamation-octagon-fill',
  info: 'bi-info-circle-fill',
  warning: 'bi-exclamation-triangle-fill',
};

const TOAST_ALERT_CLASS: Record<ToastKind, string> = {
  success: 'alert-success',
  error: 'alert-danger',
  info: 'alert-info',
  warning: 'alert-warning',
};

function ToastRegion({ toasts }: { toasts: ToastEntry[] }): React.JSX.Element {
  return (
    <div
      className="mh-toast-region"
      role="region"
      aria-label="Notifications"
      aria-live="polite"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} />
      ))}
    </div>
  );
}

function ToastItem({ toast: t }: { toast: ToastEntry }): React.JSX.Element {
  useEffect(() => {
    if (t.durationMs <= 0) return;
    const handle = window.setTimeout(() => store.dismissToast(t.id), t.durationMs);
    return () => window.clearTimeout(handle);
  }, [t.id, t.durationMs]);

  // Errors and warnings deserve assertive announcement; status updates
  // (success/info) are polite via the region wrapper.
  const role = t.kind === 'error' || t.kind === 'warning' ? 'alert' : 'status';
  return (
    <div className={`alert ${TOAST_ALERT_CLASS[t.kind]} mh-toast d-flex align-items-start`} role={role}>
      <i className={`bi ${TOAST_ICON[t.kind]} me-2 flex-shrink-0`} aria-hidden="true" />
      <div className="flex-grow-1" style={{ whiteSpace: 'pre-line' }}>{t.message}</div>
      <button
        type="button"
        className="btn-close ms-2 flex-shrink-0"
        aria-label="Dismiss notification"
        onClick={() => store.dismissToast(t.id)}
      />
    </div>
  );
}
